import { Lang } from "@/data/translation";
import { extractCapitalFlowData } from "@/helpers/capital-flow/extract-capital-flow-data";
import { extractCostEstimationData } from "@/helpers/cost-estimation/extract-cost-estimation-data";
import { extractCustomPortfolioWeightsData } from "@/helpers/custom-portfolio/extract-custom-portfolio-weights-chart";
import { extractPortfolioReturnsData as extractCustomPortfolioReturnsData } from "@/helpers/custom-portfolio/extract-portfolio-returns-data";
import { extractFinancialIndicatorData } from "@/helpers/financial-indicator/extract-financial-indicator-data";
import { transformFinancialIndicatorData } from "@/helpers/financial-indicator/transform-financial-Indicator-data";
import { extractHoldingRatioData } from "@/helpers/holding-ratio/extract-holdings-ratio-data";
import { extractIndustrialDistributionData } from "@/helpers/holding-ratio/extract-industrial-distribution-data";
import { getChipBasicInfo } from "@/services/13f/chip-basic-info";
import { getInvestorTop20Graph } from "@/services/13f/cost-estimation";
import { getInvestorPortfolioHoldings } from "@/services/13f/portfolio-distribution";
import {
  getPortfolioReturns,
  PortfolioReturnsPeriod,
} from "@/services/custom-portfolio/custom-portfolio-returns";
import { getPortfolioWeights } from "@/services/custom-portfolio/custom-portfolio-weights";
import {
  getFinancialIndicator,
  Indicator,
} from "@/services/financial-indicator";
import {
  ChartKey13F,
  ChartType,
  Chart13FInstanceParams,
  CustomPortfolioInstanceParams,
  ChartKeyCustomPortfolio,
  ChartComponentParams,
} from "@/types/chart";
import { ChartData } from "chart.js";
import dayjs, { ManipulateType } from "dayjs";
import { useState, useEffect } from "react";

type Params = {
  lang: Lang;
  enabled: boolean;
  symbol?: string;
  chartType: ChartType;
  portfolioReturnsPeriod?: PortfolioReturnsPeriod;
} & Partial<
  Pick<
    ChartComponentParams,
    | "chartKey"
    | "timeFrame"
    | "range"
    | "chart13FParams"
    | "customPortfolioParams"
  >
>;

export const useFetchData = (params: Params) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const { enabled, ...restParams } = params;
  useEffect(() => {
    if (!enabled) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const chartData = await fetchChartData(restParams);
        setData(chartData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("An error occurred while fetching data")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [enabled, ...Object.values(restParams)]);

  return { data, loading, error };
};

const fetchChartData = async ({
  lang,
  chartKey,
  chartType,
  symbol,
  timeFrame,
  range,
  chart13FParams,
  customPortfolioParams,
  portfolioReturnsPeriod,
}: Omit<Params, "enabled">): Promise<ChartData | null> => {
  if (
    chartKey === "13f_investor_holding_cost" ||
    chartKey === "13f_investor_holding_weights" ||
    chartKey === "13f_investor_industrial_distribution" ||
    chartKey === "13f_symbol_capital_flow"
  ) {
    return await fetch13FChartData(chartKey as ChartKey13F, {
      chartType,
      lang,
      chart13FParams,
    });
  }

  if (
    chartKey === "custom_portfolio_returns" ||
    chartKey === "custom_portfolio_weights"
  ) {
    return await fetchCustomPortfolioChartData(
      chartKey as ChartKeyCustomPortfolio,
      {
        chartType,
        lang,
        customPortfolioParams,
        portfolioReturnsPeriod,
      }
    );
  }

  return await fetchIndicatorChartData(chartKey as Indicator, {
    chartType,
    lang,
    symbol,
    timeFrame,
    range,
  });
};

const fetchCustomPortfolioChartData = async (
  chartKey: ChartKeyCustomPortfolio,
  params: {
    lang: Lang;
    chartType: ChartType;
    customPortfolioParams?: CustomPortfolioInstanceParams;
    portfolioReturnsPeriod?: PortfolioReturnsPeriod;
  }
): Promise<ChartData | null> => {
  const { customPortfolioParams, lang, portfolioReturnsPeriod } = params;
  const portfolioId = customPortfolioParams?.portfolioId;
  if (!portfolioId) return null;

  switch (chartKey) {
    case "custom_portfolio_returns": {
      if (!portfolioReturnsPeriod) return null;
      const portfolioReturns = await getPortfolioReturns({
        lang,
        portfolioId,
      });
      return extractCustomPortfolioReturnsData({
        lang,
        data: portfolioReturns,
        period: portfolioReturnsPeriod,
      });
    }
    case "custom_portfolio_weights": {
      const portfolioReturns = await getPortfolioWeights({
        lang,
        portfolioId,
      });
      return extractCustomPortfolioWeightsData({
        data: portfolioReturns,
      });
    }
    default:
      return null;
  }
};

const fetch13FChartData = async (
  chartKey: ChartKey13F,
  params: {
    lang: Lang;
    chartType: ChartType;
    chart13FParams?: Chart13FInstanceParams;
  }
): Promise<ChartData | null> => {
  const { chart13FParams, lang } = params;
  if (!chart13FParams) return null;

  switch (chartKey) {
    case "13f_investor_holding_cost": {
      const costData = await getInvestorTop20Graph({
        investor: chart13FParams.investor ?? "",
        symbol: chart13FParams.symbol ?? "",
        lang,
      });
      return extractCostEstimationData({ data: costData, lang });
    }

    case "13f_investor_holding_weights": {
      const holdingData = await getInvestorPortfolioHoldings<"holding-ratio">({
        investor: chart13FParams.investor ?? "",
        category: "holding-ratio",
        lang,
      });
      return extractHoldingRatioData({ lang: "zh", data: holdingData });
    }

    case "13f_investor_industrial_distribution": {
      const holdingData =
        await getInvestorPortfolioHoldings<"industrial-distribution">({
          investor: chart13FParams.investor ?? "",
          category: "industrial-distribution",
          lang,
        });
      return extractIndustrialDistributionData({
        lang: "zh",
        data: holdingData,
      });
    }

    case "13f_symbol_capital_flow": {
      const flowData = await getChipBasicInfo({
        symbol: chart13FParams.symbol ?? "",
        quarterAmount: 20,
        lang,
      });
      return extractCapitalFlowData({ lang: "zh", data: flowData });
    }

    default:
      return null;
  }
};

const fetchIndicatorChartData = async (
  indicator: Indicator,
  params: {
    lang: Lang;
    chartType: ChartType;
    symbol?: string;
    timeFrame?: ChartComponentParams["timeFrame"];
    range?: ChartComponentParams["range"];
  }
): Promise<ChartData | null> => {
  const { symbol, timeFrame, range, lang, chartType } = params;
  const { fromDate, toDate } = getDateFromRange({
    range,
  });
  const response = await getFinancialIndicator({
    indicator,
    symbol: symbol ?? "",
    sort: "asc",
    ...(timeFrame && { timeFrame }),
    ...(fromDate && { fromDate }),
    ...(toDate && { toDate }),
  });
  const extractedData = extractFinancialIndicatorData({ lang, data: response });
  return transformFinancialIndicatorData({
    extractedData,
    chartType,
    chartKey: indicator,
  });
};

const getDateFromRange = ({
  range,
}: {
  range: ChartComponentParams["range"];
}) => {
  if (!range) return {};

  const rangeNumber = +range.replace("y", "");
  const rangeUnit = range.replace(`${rangeNumber}`, "");
  const fromDate = dayjs()
    .subtract(rangeNumber, rangeUnit as ManipulateType)
    .format("YYYY-MM-DD");
  const toDate = dayjs().format("YYYY-MM-DD");

  return { fromDate, toDate };
};

export default useFetchData;
